import {
	disconnectUserConnector,
	startUserConnectorOAuth,
	updateUserConnectors,
} from '../../lib/apiRequests';
import { finishLoading, showNonBlockingMessage, startLoading } from '../../lib/ui/uiUtils';

type ConnectorPreset = {
	id:
		| 'notion'
		| 'slack'
		| 'github'
		| 'supabase'
		| 'vercel'
		| 'gemini'
		| 'claude'
		| 'pubmed'
		| 'gdelt'
		| 'custom';
	label: string;
	description: string;
	authType: 'static_headers' | 'oauth2_pkce';
	defaultStatus: 'not_connected' | 'configured' | 'connecting' | 'connected' | 'expired' | 'error';
	transport: 'streamable-http' | 'sse';
	serverUrl: string;
	urlPlaceholder: string;
	requestHeaders?: Record<string, string>;
	allowedToolNamesPlaceholder?: string;
	scopes?: string[];
	helperText?: string;
};

type ConnectorPayload = {
	id: string;
	provider: ConnectorPreset['id'];
	label: string;
	authType: ConnectorPreset['authType'];
	status: ConnectorPreset['defaultStatus'];
	enabled: boolean;
	transport: ConnectorPreset['transport'];
	serverUrl: string;
	requestHeaders?: Record<string, string>;
	scopes?: string[];
	allowedToolNames?: string[];
	lastError?: string;
};

const presetsElement = document.getElementById('connector-presets-json');
const presets: ConnectorPreset[] = presetsElement?.textContent
	? (JSON.parse(presetsElement.textContent) as ConnectorPreset[])
	: [];
const presetsById = new Map(presets.map((preset) => [preset.id, preset] as const));

const list = document.getElementById('connectors-list');
const template = document.getElementById('connector-item-template') as HTMLTemplateElement | null;
const emptyState = document.querySelector('.connector-empty-state');
const addCustomButton = document.getElementById('add-custom-connector');
const saveButton = document.getElementById('save-connectors');
const userId = document.body.dataset.userId!;

const updateEmptyState = () => {
	if (!emptyState || !list) return;
	emptyState.classList.toggle('hidden', list.querySelectorAll('.connector-item').length > 0);
};

const syncAuthModeUi = (item: Element) => {
	const authType = item.querySelector<HTMLInputElement>('.connector-auth-type')?.value || 'static_headers';
	item.classList.toggle('oauth-connector', authType === 'oauth2_pkce');
	item.classList.toggle('static-connector', authType !== 'oauth2_pkce');
};

const syncConnectorStatusUi = (item: Element) => {
	const status = item.querySelector<HTMLInputElement>('.connector-status')?.value || 'not_connected';
	const badge = item.querySelector<HTMLElement>('.connector-status-badge');
	const connectButton = item.querySelector<HTMLButtonElement>('.connector-connect');
	const disconnectButton = item.querySelector<HTMLButtonElement>('.connector-disconnect');

	if (badge) {
		badge.textContent = status.replace(/_/g, ' ');
	}
	if (connectButton) {
		connectButton.textContent = status === 'connected' ? 'Reconnect' : 'Connect';
		connectButton.disabled = status === 'connecting';
	}
	if (disconnectButton) {
		disconnectButton.disabled = status === 'connecting';
	}
};

const attachRemoveHandler = (item: Element) => {
	item.querySelector('.connector-remove')?.addEventListener('click', () => {
		item.remove();
		updateEmptyState();
	});
};

const attachConnectorBehaviors = (item: Element) => {
	attachRemoveHandler(item);
	syncAuthModeUi(item);
	syncConnectorStatusUi(item);

	item.querySelector('.connector-connect')?.addEventListener('click', async () => {
		const connectorId = item.querySelector<HTMLInputElement>('.connector-id')?.value.trim();
		if (!connectorId) {
			showNonBlockingMessage('Save the connector first so Flowy has an id to connect.');
			return;
		}

		try {
			startLoading('Preparing OAuth connection...');
			const connectors = collectConnectors();
			await updateUserConnectors(userId, connectors);
			const { redirectUrl } = await startUserConnectorOAuth(userId, connectorId);
			globalThis.location.href = redirectUrl;
		} catch (error) {
			const message =
				error instanceof Error ? error.message : 'Unable to start OAuth connection.';
			showNonBlockingMessage(message);
			finishLoading();
		}
	});

	item.querySelector('.connector-disconnect')?.addEventListener('click', async () => {
		const connectorId = item.querySelector<HTMLInputElement>('.connector-id')?.value.trim();
		if (!connectorId) {
			showNonBlockingMessage('This connector has not been saved yet.');
			return;
		}

		try {
			startLoading('Disconnecting connector...');
			await disconnectUserConnector(userId, connectorId);
			const statusInput = item.querySelector<HTMLInputElement>('.connector-status');
			const lastErrorInput = item.querySelector<HTMLInputElement>('.connector-last-error');
			if (statusInput) statusInput.value = 'not_connected';
			if (lastErrorInput) lastErrorInput.value = '';
			syncConnectorStatusUi(item);
			showNonBlockingMessage('Connector disconnected.', [], true);
		} catch (error) {
			const message = error instanceof Error ? error.message : 'Unable to disconnect connector.';
			showNonBlockingMessage(message);
		} finally {
			finishLoading();
		}
	});
};

const createConnectorItem = (preset?: ConnectorPreset) => {
	if (!template || !list) return;

	const fragment = template.content.cloneNode(true) as DocumentFragment;
	const item = fragment.querySelector('.connector-item');
	if (!item) return;

	const nowId =
		globalThis.crypto?.randomUUID?.() ||
		`connector-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

	const idInput = item.querySelector<HTMLInputElement>('.connector-id');
	const providerInput = item.querySelector<HTMLInputElement>('.connector-provider');
	const authTypeInput = item.querySelector<HTMLInputElement>('.connector-auth-type');
	const statusInput = item.querySelector<HTMLInputElement>('.connector-status');
	const labelInput = item.querySelector<HTMLInputElement>('.connector-label');
	const transportSelect = item.querySelector<HTMLSelectElement>('.connector-transport');
	const urlInput = item.querySelector<HTMLInputElement>('.connector-url');
	const headersInput = item.querySelector<HTMLTextAreaElement>('.connector-headers');
	const toolsInput = item.querySelector<HTMLInputElement>('.connector-tools');
	const scopesInput = item.querySelector<HTMLInputElement>('.connector-scopes');
	const helper = item.querySelector<HTMLElement>('.connector-helper-text');
	const badge = item.querySelector<HTMLElement>('.connector-status-badge');
	const oauthNotice = item.querySelector<HTMLElement>('.oauth-connector-notice');

	if (idInput) idInput.value = nowId;

	if (preset) {
		if (providerInput) providerInput.value = preset.id;
		if (authTypeInput) authTypeInput.value = preset.authType;
		if (statusInput) statusInput.value = preset.defaultStatus;
		if (labelInput) labelInput.value = preset.label;
		if (transportSelect) transportSelect.value = preset.transport;
		if (urlInput) {
			urlInput.value = preset.serverUrl;
			urlInput.placeholder = preset.urlPlaceholder;
		}
		if (headersInput) {
			headersInput.value = JSON.stringify(preset.requestHeaders || {}, null, 2);
		}
		if (toolsInput && preset.allowedToolNamesPlaceholder) {
			toolsInput.placeholder = preset.allowedToolNamesPlaceholder;
		}
		if (scopesInput) {
			scopesInput.value = (preset.scopes || []).join(', ');
		}
		if (helper && preset.helperText) {
			helper.textContent = preset.helperText;
		}
		if (oauthNotice && preset.authType === 'oauth2_pkce') {
			oauthNotice.textContent = 'OAuth connectors redirect through the provider and store tokens server-side.';
		}
	}

	attachConnectorBehaviors(item);
	list.appendChild(fragment);
	updateEmptyState();
	labelInput?.focus();
};

const parseHeaders = (rawValue: string, connectorName: string): Record<string, string> | undefined => {
	const trimmed = rawValue.trim();
	if (!trimmed || trimmed === '{}') return undefined;

	let parsed: unknown;
	try {
		parsed = JSON.parse(trimmed);
	} catch {
		throw new Error(`Connector "${connectorName}" has invalid header JSON.`);
	}

	if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
		throw new Error(`Connector "${connectorName}" headers must be a JSON object.`);
	}

	const entries = Object.entries(parsed)
		.map(([key, value]) => [key.trim(), typeof value === 'string' ? value.trim() : ''] as const)
		.filter((entry): entry is [string, string] => Boolean(entry[0]) && Boolean(entry[1]));

	return entries.length ? Object.fromEntries(entries) : undefined;
};

const collectConnectors = (): ConnectorPayload[] => {
	if (!list) return [];

	return Array.from(list.querySelectorAll('.connector-item')).map((item, index) => {
		const id = item.querySelector<HTMLInputElement>('.connector-id')?.value.trim() || '';
		const provider =
			(item.querySelector<HTMLInputElement>('.connector-provider')?.value.trim() as ConnectorPreset['id']) ||
			'custom';
		const label = item.querySelector<HTMLInputElement>('.connector-label')?.value.trim() || '';
		const authType =
			(item.querySelector<HTMLInputElement>('.connector-auth-type')?.value.trim() as ConnectorPreset['authType']) ||
			'static_headers';
		const status =
			(item.querySelector<HTMLInputElement>('.connector-status')?.value.trim() as ConnectorPreset['defaultStatus']) ||
			(authType === 'oauth2_pkce' ? 'not_connected' : 'configured');
		const serverUrl = item.querySelector<HTMLInputElement>('.connector-url')?.value.trim() || '';
		const transport =
			(item.querySelector<HTMLSelectElement>('.connector-transport')?.value as ConnectorPreset['transport']) ||
			'streamable-http';
		const enabled = item.querySelector<HTMLInputElement>('.connector-enabled-input')?.checked !== false;
		const headersRaw = item.querySelector<HTMLTextAreaElement>('.connector-headers')?.value || '{}';
		const toolNamesRaw = item.querySelector<HTMLInputElement>('.connector-tools')?.value || '';
		const scopesRaw = item.querySelector<HTMLInputElement>('.connector-scopes')?.value || '';
		const lastError = item.querySelector<HTMLInputElement>('.connector-last-error')?.value.trim() || '';
		const connectorName = label || `connector ${index + 1}`;

		if (!id) throw new Error(`Connector "${connectorName}" is missing an id.`);
		if (!label) throw new Error(`Connector ${index + 1} is missing a label.`);
		if (!serverUrl) throw new Error(`Connector "${connectorName}" is missing an endpoint URL.`);
		const parsedHeaders = parseHeaders(headersRaw, connectorName);

		return {
			id,
			provider,
			label,
			authType,
			status,
			enabled,
			transport,
			serverUrl,
			...(parsedHeaders ? { requestHeaders: parsedHeaders } : {}),
			...(scopesRaw.trim()
				? {
						scopes: scopesRaw
							.split(',')
							.map((value) => value.trim())
							.filter(Boolean),
					}
				: {}),
			...(toolNamesRaw.trim()
				? {
						allowedToolNames: toolNamesRaw
							.split(',')
							.map((value) => value.trim())
							.filter(Boolean),
					}
				: {}),
			...(lastError ? { lastError } : {}),
		};
	});
};

document.querySelectorAll('.connector-item').forEach((item) => attachConnectorBehaviors(item));
updateEmptyState();

addCustomButton?.addEventListener('click', () => createConnectorItem(presetsById.get('custom')));

document.querySelectorAll<HTMLElement>('.preset-card').forEach((card) => {
	card.addEventListener('click', () => {
		const presetId = card.dataset.presetId || '';
		createConnectorItem(presetsById.get(presetId as ConnectorPreset['id']));
	});
});

saveButton?.addEventListener('click', async () => {
	try {
		startLoading('Saving connectors...');
		await updateUserConnectors(userId, collectConnectors());
		showNonBlockingMessage('Connectors saved.', [], true);
	} catch (error) {
		const message = error instanceof Error ? error.message : 'Unable to save connectors.';
		showNonBlockingMessage(message);
	} finally {
		finishLoading();
	}
});

finishLoading();
